import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/lib/supabase";

interface UserSearchResult {
  id: string;
  nickname: string;
  email: string;
  points: number;
}

export default function AdminPointsPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<UserSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserSearchResult | null>(null);
  const [adjustType, setAdjustType] = useState<"적립" | "차감">("적립");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Search users with debounce
  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);

    if (searchTimer.current) {
      clearTimeout(searchTimer.current);
    }

    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    searchTimer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const { data, error } = await supabase
          .from("profiles")
          .select("id, nickname, email, points")
          .or(`nickname.ilike.%${query}%,email.ilike.%${query}%`)
          .limit(10);

        if (error) throw error;
        setSearchResults(data || []);
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
  }, []);

  const handleSelectUser = useCallback((user: UserSearchResult) => {
    setSelectedUser(user);
    setSearchQuery("");
    setSearchResults([]);
  }, []);

  // Submit point adjustment
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser || !amount || !reason.trim()) {
      setMessage({ type: "error", text: "모든 필드를 입력해주세요" });
      return;
    }

    const pointAmount = parseInt(amount, 10);
    if (isNaN(pointAmount) || pointAmount <= 0) {
      setMessage({ type: "error", text: "올바른 포인트 금액을 입력해주세요" });
      return;
    }

    setSubmitting(true);
    setMessage(null);

    try {
      const finalAmount = adjustType === "차감" ? -pointAmount : pointAmount;

      // Get current points
      const { data: profile, error: profileError } = await supabase
        .from("profiles")
        .select("points")
        .eq("id", selectedUser.id)
        .maybeSingle();

      if (profileError) throw profileError;

      const currentPoints = profile?.points ?? 0;
      const newPoints = currentPoints + finalAmount;

      if (adjustType === "차감" && newPoints < 0) {
        setMessage({ type: "error", text: "차감 후 포인트가 0보다 작을 수 없습니다" });
        setSubmitting(false);
        return;
      }

      // Update points
      const { error: updateError } = await supabase
        .from("profiles")
        .update({ points: newPoints })
        .eq("id", selectedUser.id);

      if (updateError) throw updateError;

      // Record transaction
      const { error: txnError } = await supabase
        .from("point_transactions")
        .insert({
          user_id: selectedUser.id,
          type: finalAmount >= 0 ? "적립" : "사용",
          detail: `관리자 ${adjustType}: ${reason}`,
          amount: finalAmount,
        });

      if (txnError) throw txnError;

      // Update local state
      setSelectedUser({ ...selectedUser, points: newPoints });

      setMessage({
        type: "success",
        text: `${selectedUser.nickname || selectedUser.email}님에게 ${pointAmount}P ${adjustType === "적립" ? "지급" : "차감"} 완료! (현재 ${newPoints}P)`,
      });

      setAmount("");
      setReason("");
    } catch {
      setMessage({ type: "error", text: "처리 중 오류가 발생했습니다" });
    } finally {
      setSubmitting(false);
    }
  }, [selectedUser, amount, reason, adjustType]);

  return (
    <div className="space-y-5">
      {/* Header */}
      <div>
        <h2 className="text-base font-bold text-foreground-800">포인트 관리</h2>
        <p className="text-xs text-foreground-600 mt-0.5">사용자 포인트를 수동으로 지급하거나 차감할 수 있습니다</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Search User */}
        <div className="bg-background-100 rounded-xl border border-background-300 p-5">
          <h2 className="text-sm font-bold text-foreground-800 mb-4 flex items-center gap-2">
            <span className="w-5 h-5 flex items-center justify-center text-foreground-600">
              <i className="ri-user-search-line" />
            </span>
            사용자 검색
          </h2>

          <div className="relative">
            <input
              type="text"
              placeholder="닉네임 또는 이메일로 검색"
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              className="w-full bg-background-200 border border-background-300 rounded-lg px-4 py-2.5 text-sm text-foreground-700 placeholder-foreground-500 outline-none focus:border-primary-400 transition-colors"
            />
            {searching && (
              <div className="absolute right-3 top-1/2 -translate-y-1/2">
                <div className="w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
              </div>
            )}
          </div>

          {/* Search Results */}
          {searchResults.length > 0 && (
            <div className="mt-3 border border-background-300 rounded-lg overflow-hidden">
              {searchResults.map((user) => (
                <button
                  key={user.id}
                  onClick={() => handleSelectUser(user)}
                  className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-background-200/70 transition-colors cursor-pointer border-b border-background-300 last:border-0"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-8 h-8 rounded-full bg-primary-500/20 flex items-center justify-center flex-shrink-0">
                      <i className="ri-user-fill text-primary-400 text-sm" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-foreground-700 truncate">
                        {user.nickname || "익명"}
                      </p>
                      <p className="text-[10px] text-foreground-600 truncate">{user.email}</p>
                    </div>
                  </div>
                  <span className="text-xs font-bold text-accent-500 flex-shrink-0 ml-3">
                    {user.points}P
                  </span>
                </button>
              ))}
            </div>
          )}

          {searchQuery && !searching && searchResults.length === 0 && (
            <p className="mt-3 text-xs text-foreground-600 text-center py-4">
              검색 결과가 없습니다
            </p>
          )}
        </div>

        {/* Point Adjustment */}
        <div>
          {selectedUser ? (
            <div className="bg-background-100 rounded-xl border border-background-300 p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-sm font-bold text-foreground-800 flex items-center gap-2">
                  <span className="w-5 h-5 flex items-center justify-center text-foreground-600">
                    <i className="ri-exchange-funds-line" />
                  </span>
                  포인트 조정
                </h2>
                <button
                  onClick={() => setSelectedUser(null)}
                  className="text-xs text-foreground-600 hover:text-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-close-line mr-1" />
                  초기화
                </button>
              </div>

              {/* Selected user info */}
              <div className="flex items-center gap-3 p-3 bg-background-200 rounded-lg mb-4">
                <div className="w-9 h-9 rounded-full bg-primary-500/20 flex items-center justify-center flex-shrink-0">
                  <i className="ri-user-fill text-primary-400" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-bold text-foreground-700 truncate">
                    {selectedUser.nickname || "익명"}
                  </p>
                  <p className="text-[10px] text-foreground-600 truncate">{selectedUser.email}</p>
                </div>
                <div className="text-right flex-shrink-0">
                  <span className="text-sm font-black text-accent-500">{selectedUser.points}P</span>
                </div>
              </div>

              {/* Adjustment form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Type selector */}
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setAdjustType("적립")}
                    className={`flex-1 py-2.5 text-xs font-bold rounded-lg border transition-colors whitespace-nowrap cursor-pointer ${
                      adjustType === "적립"
                        ? "bg-accent-500 text-foreground-50 border-accent-500"
                        : "bg-background-200 text-foreground-600 border-background-300 hover:bg-background-300"
                    }`}
                  >
                    <i className="ri-add-circle-line mr-1" />
                    적립
                  </button>
                  <button
                    type="button"
                    onClick={() => setAdjustType("차감")}
                    className={`flex-1 py-2.5 text-xs font-bold rounded-lg border transition-colors whitespace-nowrap cursor-pointer ${
                      adjustType === "차감"
                        ? "bg-red-500 text-foreground-50 border-red-500"
                        : "bg-background-200 text-foreground-600 border-background-300 hover:bg-background-300"
                    }`}
                  >
                    <i className="ri-indeterminate-circle-line mr-1" />
                    차감
                  </button>
                </div>

                {/* Amount */}
                <div>
                  <label className="block text-xs font-medium text-foreground-700 mb-1.5">
                    {adjustType === "적립" ? "지급" : "차감"} 포인트
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min="1"
                      placeholder="포인트 입력"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      className="flex-1 bg-background-200 border border-background-300 rounded-lg px-4 py-2.5 text-sm text-foreground-700 outline-none focus:border-primary-400 transition-colors"
                    />
                    <span className="text-sm font-bold text-foreground-600 flex-shrink-0">P</span>
                  </div>
                  {/* Quick amounts */}
                  <div className="flex gap-1.5 mt-2">
                    {[10, 50, 100, 500, 1000].map((v) => (
                      <button
                        key={v}
                        type="button"
                        onClick={() => setAmount(String(v))}
                        className={`px-2.5 py-1 text-[10px] rounded-md border transition-colors whitespace-nowrap cursor-pointer ${
                          amount === String(v)
                            ? "bg-primary-500/20 border-primary-400 text-primary-400"
                            : "bg-background-200 border-background-300 text-foreground-600 hover:bg-background-300"
                        }`}
                      >
                        {v}P
                      </button>
                    ))}
                  </div>
                </div>

                {/* Reason */}
                <div>
                  <label className="block text-xs font-medium text-foreground-700 mb-1.5">
                    사유
                  </label>
                  <input
                    type="text"
                    placeholder={`${adjustType === "적립" ? "지급" : "차감"} 사유를 입력하세요`}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    className="w-full bg-background-200 border border-background-300 rounded-lg px-4 py-2.5 text-sm text-foreground-700 outline-none focus:border-primary-400 transition-colors"
                  />
                </div>

                {/* Message */}
                {message && (
                  <div className={`px-4 py-3 rounded-lg text-xs font-medium ${
                    message.type === "success"
                      ? "bg-accent-100/80 text-accent-800 border border-accent-300/60"
                      : "bg-red-500/10 text-red-400 border border-red-500/30"
                  }`}>
                    {message.text}
                  </div>
                )}

                {/* Submit */}
                <button
                  type="submit"
                  disabled={submitting || !selectedUser}
                  className="w-full py-2.5 text-sm font-bold rounded-lg transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 bg-primary-500 text-foreground-50 hover:bg-primary-600"
                >
                  {submitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 border-2 border-foreground-50 border-t-transparent rounded-full animate-spin" />
                      처리 중...
                    </span>
                  ) : (
                    <span>
                      {adjustType === "적립" ? "+" : "-"}{amount || 0}P {adjustType === "적립" ? "지급" : "차감"}하기
                    </span>
                  )}
                </button>
              </form>
            </div>
          ) : (
            <div className="bg-background-100 rounded-xl border border-background-300 p-5 flex flex-col items-center justify-center py-12">
              <div className="w-14 h-14 rounded-full bg-background-200 flex items-center justify-center mb-3">
                <i className="ri-user-search-line text-2xl text-foreground-600" />
              </div>
              <p className="text-sm font-medium text-foreground-700 mb-1">사용자를 검색하세요</p>
              <p className="text-xs text-foreground-600">닉네임 또는 이메일로 검색 후 선택하세요</p>
            </div>
          )}
        </div>
      </div>

      {/* Danger Zone */}
      <div className="mt-6 bg-background-100 rounded-xl border border-red-500/20 p-5">
        <h2 className="text-sm font-bold text-red-400 mb-2 flex items-center gap-2">
          <span className="w-5 h-5 flex items-center justify-center">
            <i className="ri-alert-line" />
          </span>
          주의사항
        </h2>
        <ul className="space-y-1.5">
          <li className="flex items-start gap-2 text-xs text-foreground-600">
            <span className="text-red-400 mt-0.5 flex-shrink-0">·</span>
            <span>포인트 지급/차감 내역은 <strong className="text-foreground-700">point_transactions</strong> 테이블에 자동 기록됩니다.</span>
          </li>
          <li className="flex items-start gap-2 text-xs text-foreground-600">
            <span className="text-red-400 mt-0.5 flex-shrink-0">·</span>
            <span>차감 시 현재 보유 포인트보다 많이 차감할 수 없습니다.</span>
          </li>
          <li className="flex items-start gap-2 text-xs text-foreground-600">
            <span className="text-red-400 mt-0.5 flex-shrink-0">·</span>
            <span>모든 관리자 작업은 기록되며, 신중하게 처리해주세요.</span>
          </li>
        </ul>
      </div>
    </div>
  );
}